// src/components/signage/EstadoDeCarteleria.jsx
//
// Compact status banner for the Estado tab. Shows only the 3 things
// the admin needs at a glance:
//
//   1. Master switch: ON / OFF (large Ant Switch, click to toggle)
//   2. Today's rotor: number of campaigns scheduled + their names
//   3. One connected device: yes/no (badge from /signage/health)
//
// Everything else (per-device list, last-seen IPs, heartbeat) lives in
// a Popover that opens on hover over the device badge. The original
// SignageHealthCard is no longer mounted in this layout.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge, Button, Popover, Skeleton, Switch, Tooltip, message } from 'antd';
import { ReloadOutlined, CheckCircleTwoTone, CloseCircleTwoTone, ThunderboltOutlined } from '@ant-design/icons';
import {
    getSystemStatus,
    enableSystem,
    disableSystem,
    getSignageHealth,
} from '../../services/signageService';
import { heartbeatDevice } from '../../services/deviceService';
import { useAuth } from '../../context/AuthContext';
import './EstadoDeCarteleria.css';

const POLL_INTERVAL_MS = 30_000;
// A device is "real" (not a leftover from a test/dev cycle) if it was
// seen in the last 24 h. Stale-older-than-24h rows are noise; filter them
// out of the popover and the count badge.
const ACTIVE_DEVICE_WINDOW_SEC = 24 * 60 * 60;

function formatAgo(seconds) {
    if (seconds == null) return '—';
    if (seconds < 60) return `hace ${seconds}s`;
    if (seconds < 3600) return `hace ${Math.floor(seconds / 60)}m`;
    if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)}h`;
    return `hace ${Math.floor(seconds / 86_400)}d`;
}

// Argentina is fixed UTC-3, no DST. Format in 24h, in ART, so the
// admin always sees wall-clock time regardless of their browser TZ.
function formatArgTime(date) {
    if (!date) return '';
    return date.toLocaleTimeString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export default function EstadoDeCarteleria({ systemStatus, setSystemStatus, todayCount, onSnackbar }) {
    const { user: authUser } = useAuth();
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [refreshingId, setRefreshingId] = useState(null);
    const [now, setNow] = useState(new Date());
    const pollRef = useRef(null);

    // Only ClientAdministrator can refresh a device session. The original
    // SignageHealthCard had the same guard.
    const canRefresh = authUser?.role === 'ClientAdministrator';

    // Filter to "real" devices — anything with lastSeen within the window.
    // Filters out the long tail of stale test devices that the screenshot
    // was showing (44d, 43d, etc.).
    const realDevices = (health?.devices || []).filter(d => {
        if (d.status === 'REVOKED' || d.status === 'EXPIRED') return false;
        const sec = d.lastSeen?.secondsAgo;
        return typeof sec === 'number' && sec < ACTIVE_DEVICE_WINDOW_SEC;
    });
    const primaryDevice = realDevices[0] || null;

    const fetchHealth = useCallback(async () => {
        try {
            const data = await getSignageHealth();
            setHealth(data);
        } catch {
            // health endpoint failure is non-fatal for the banner
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHealth();
        function startPolling() {
            if (pollRef.current) return;
            pollRef.current = setInterval(() => {
                fetchHealth();
                setNow(new Date());
            }, POLL_INTERVAL_MS);
        }
        function stopPolling() {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
        }
        function onVisibility() {
            if (document.hidden) stopPolling();
            else { fetchHealth(); startPolling(); }
        }
        startPolling();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fetchHealth]);

    const enabled = !!systemStatus?.enabled;
    const paused = !!systemStatus?.paused;
    const onlineCount = realDevices.filter(d => d.status === 'ONLINE').length;
    const oneOnline = onlineCount > 0;

    const handleRefreshSession = useCallback(async (device) => {
        if (refreshingId || !device) return;
        setRefreshingId(device.deviceId);
        try {
            const r = await heartbeatDevice(device.deviceId);
            onSnackbar?.(
                r?.message || `Sesión refrescada para "${device.label}" — expira en 30 días.`,
                'success'
            );
            await fetchHealth();
        } catch (e) {
            const code = e?.response?.data?.code || 'ERROR';
            const msg  = e?.response?.data?.error || e?.message || 'Error desconocido';
            onSnackbar?.(`No se pudo refrescar (${code}): ${msg}`, 'error');
        } finally {
            setRefreshingId(null);
        }
    }, [refreshingId, fetchHealth, onSnackbar]);

    const handleToggle = useCallback(async (next) => {
        setToggling(true);
        try {
            if (next) {
                const r = await enableSystem();
                setSystemStatus(s => ({ ...s, enabled: true, paused: false }));
                onSnackbar?.(r.message || 'Cartelería activada', 'success');
            } else {
                const r = await disableSystem();
                setSystemStatus(s => ({ ...s, enabled: false, paused: true }));
                onSnackbar?.(r.message || 'Cartelería desactivada', 'info');
            }
        } catch (e) {
            onSnackbar?.('Error: ' + (e.response?.data?.error || e.message), 'error');
        } finally {
            setToggling(false);
        }
    }, [setSystemStatus, onSnackbar]);

    const devicePopover = (
        <div className="estado-device-popover">
            <div className="estado-device-popover__row">
                <strong>{health?.organization?.organizationName || '—'}</strong>
            </div>
            <div className="estado-device-popover__row">
                {loading && <Skeleton.Input active size="small" style={{ width: 120 }} />}
                {!loading && realDevices.length === 0 && (
                    <span style={{ color: '#999' }}>Sin dispositivos activos</span>
                )}
                {!loading && realDevices.map(d => {
                    const canRefreshThis = canRefresh && d.status !== 'EXPIRED';
                    return (
                        <div key={d.deviceId} className="estado-device-popover__device">
                            {d.status === 'ONLINE' ? (
                                <CheckCircleTwoTone twoToneColor="#52c41a" />
                            ) : (
                                <CloseCircleTwoTone twoToneColor={d.status === 'EXPIRED' ? '#ff4d4f' : '#999'} />
                            )}
                            <strong>{d.label}</strong>
                            <small>…{d.tokenLastFour}</small>
                            <small>{formatAgo(d.lastSeen?.secondsAgo)}</small>
                            {canRefreshThis && (
                                <Tooltip title="Forzar un JWT fresco de 30 días">
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<ThunderboltOutlined />}
                                        loading={refreshingId === d.deviceId}
                                        onClick={() => handleRefreshSession(d)}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="estado-carteleria">
            <div className="estado-carteleria__header">
                <h3>Estado de Cartelería</h3>
                <span className="estado-carteleria__ts">
                    {loading ? 'Cargando…' : `Actualizado ${formatArgTime(now)}`}
                </span>
                <Tooltip title="Refrescar listado">
                    <Button
                        size="small"
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={fetchHealth}
                    />
                </Tooltip>
                {canRefresh && primaryDevice && (
                    <Tooltip
                        title={
                            primaryDevice.status === 'EXPIRED'
                                ? 'La credencial subyacente expiró; emitir una nueva desde la herramienta de administración.'
                                : `Refrescar sesión de "${primaryDevice.label}" (forzar JWT fresco de 30 días)`
                        }
                    >
                        <Button
                            size="small"
                            type="primary"
                            icon={<ThunderboltOutlined />}
                            loading={refreshingId === primaryDevice.deviceId}
                            onClick={() => handleRefreshSession(primaryDevice)}
                        >
                            Refrescar sesión
                        </Button>
                    </Tooltip>
                )}
            </div>

            <div className="estado-carteleria__row">
                <div className="estado-carteleria__cell">
                    <span className="label">Signage habilitado</span>
                    <Badge
                        status={enabled ? 'success' : 'default'}
                        text={enabled ? 'Sí' : 'No'}
                    />
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Cartelería</span>
                    <Switch
                        checked={enabled}
                        onChange={handleToggle}
                        loading={toggling}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                    />
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Rotativo de hoy</span>
                    <span>
                        <strong>{todayCount}</strong>{' '}
                        {todayCount === 1 ? 'campaña' : 'campañas'}
                    </span>
                </div>

                <div className="estado-carteleria__cell">
                    <span className="label">Dispositivo</span>
                    <Popover
                        content={devicePopover}
                        title="Detalle de dispositivos"
                        trigger="hover"
                        placement="bottomRight"
                    >
                        {loading ? (
                            <Badge status="processing" text="Verificando…" />
                        ) : oneOnline ? (
                            <Badge status="success" text={realDevices.length === 1 ? '1 en línea' : `${onlineCount} en línea`} />
                        ) : (
                            <Badge status="warning" text="Sin conexión" />
                        )}
                    </Popover>
                </div>
            </div>
        </div>
    );
}
